import * as React from 'react';
import styled from 'styled-components';
import PaymentOutlinedIcon from '@material-ui/icons/PaymentOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import AutorenewIcon from '@material-ui/icons/Autorenew';

import { getPermitStatsCount } from '../api/permits';

const labels = {
  completed: 'Completed',
  withdrawn: 'Withdrawn',
};

type Tone = 'warning' | 'primary' | 'info';

const TONES: Record<Tone, { bg: string; fg: string }> = {
  warning: { bg: 'var(--warning-50)', fg: 'var(--warning-600)' },
  primary: { bg: 'var(--primary-50)', fg: 'var(--primary-600)' },
  info: { bg: 'var(--info-50)', fg: 'var(--info-600)' },
};

const ActivePermitsBlock = () => {
  const [waitingPayment, setWaitingPayment] = React.useState(0);
  const [totalActivePermits, setTotalActivePermits] = React.useState(0);
  const [inProgress, setInProgress] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    getPermitStatsCount()
      .then(response => {
        const statuses = response.data?.permitStatuses || [];
        const invoiceCount = response.data?.invoiceSummary?.sentInvoiceCount || 0;
        const approvedProposals = response.data?.approvedProposalsCount || 0;

        setWaitingPayment(invoiceCount);
        setTotalActivePermits(
          statuses
            .filter(item => item.title !== labels.completed && item.title !== labels.withdrawn)
            .reduce((acc, item) => acc + item.count, 0),
        );
        setInProgress(approvedProposals);
      })
      .finally(() => setLoading(false));
  }, []);

  const stats: Array<{ key: string; label: string; value: number; Icon: typeof PaymentOutlinedIcon; tone: Tone }> = [
    { key: 'waiting', label: 'Waiting for payment', value: waitingPayment, Icon: PaymentOutlinedIcon, tone: 'warning' },
    { key: 'active', label: 'Total active permits', value: totalActivePermits, Icon: DescriptionOutlinedIcon, tone: 'primary' },
    { key: 'progress', label: 'In progress', value: inProgress, Icon: AutorenewIcon, tone: 'info' },
  ];

  return (
    <Wrapper>
      <Header>My active permits</Header>
      <List>
        {stats.map(({ key, label, value, Icon, tone }) => (
          <StatRow key={key}>
            <IconBadge tone={tone}>
              <Icon fontSize="small" />
            </IconBadge>
            <StatText>
              <StatLabel>{label}</StatLabel>
              <StatValue>{loading ? '—' : value.toLocaleString()}</StatValue>
            </StatText>
          </StatRow>
        ))}
      </List>
    </Wrapper>
  );
};

const FONT = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif,
  'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`;

const Wrapper = styled.div`
  font-family: ${FONT};
  width: 270px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  margin-right: 10px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  @media (max-width: 820px) {
    width: 100%;
  }
`;

const Header = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  box-sizing: border-box;
`;

const IconBadge = styled.div<{ tone: Tone }>`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${({ tone }) => TONES[tone].bg};
  color: ${({ tone }) => TONES[tone].fg};

  svg {
    font-size: 22px;
  }
`;

const StatText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const StatLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const StatValue = styled.span`
  margin-top: 2px;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  color: var(--mainText);
`;

export default ActivePermitsBlock;
